import React, { KeyboardEvent } from 'react';
import {
  Accordion,
  Avatar,
  Box,
  ColumnDef,
  DataTable,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import formatDate from '@core/utils/formatDate';
import SubmissionActions from '../SubmissionActions';
import { createStyles } from './SubmissionAccordions.styles';
import {
  ResponsiveGroupedSubmissionGroups,
  ResponsiveSubmissionGroups,
  ResponsiveSubmissionItem,
} from './SubmissionAccordions.types';
import { getSubmissionCapabilities } from './submissionCapabilities';

type Props = {
  accessingAsStaff: boolean;
  groupedSubmissionGroups?: ResponsiveGroupedSubmissionGroups;
  hideNoSubmissionIdentities?: boolean;
  isEducator: boolean;
  isGroupAssignment?: boolean;
  onDeleteSubmission?: (
    submission: ResponsiveSubmissionItem
  ) => Promise<void> | void;
  onOpenSubmission?: (submission: ResponsiveSubmissionItem) => void;
  provider: string;
  submissionGroups: ResponsiveSubmissionGroups;
};

type SectionKey = keyof ResponsiveSubmissionGroups;

const sections: Array<{ key: SectionKey; label: string }> = [
  { key: 'earlySubmissions', label: 'On time Submission' },
  { key: 'lateSubmissions', label: 'Late Submission' },
  { key: 'noSubmissions', label: 'No Submission' },
];

export const getSubmissionName = ({ user }: ResponsiveSubmissionItem) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
  'Unnamed student';

const getSubmissionGrade = (submission: ResponsiveSubmissionItem) => {
  if (submission.displayGrade) {
    return submission.displayGrade.includes('NaN')
      ? 'Not graded'
      : submission.displayGrade;
  }

  return submission.receivedAt ? 'Not graded' : '-';
};

const SubmissionAccordions = ({
  accessingAsStaff,
  groupedSubmissionGroups,
  hideNoSubmissionIdentities = false,
  isEducator,
  isGroupAssignment = false,
  onDeleteSubmission,
  onOpenSubmission,
  provider,
  submissionGroups,
}: Props) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const styles = createStyles();

  const getItemDetails = (submission: ResponsiveSubmissionItem) => {
    const hasSubmission = Boolean(submission.id);
    return {
      capabilities: getSubmissionCapabilities({
        accessingAsStaff,
        hasSubmission,
        isEducator,
        provider,
      }),
      date: submission.receivedAt
        ? formatDate(submission.receivedAt)
        : 'No submission',
      grade: getSubmissionGrade(submission),
      hasSubmission,
      name: getSubmissionName(submission),
    };
  };

  const openSubmission = (submission: ResponsiveSubmissionItem) => {
    if (getItemDetails(submission).capabilities.canOpen) {
      onOpenSubmission?.(submission);
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent,
    submission: ResponsiveSubmissionItem
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openSubmission(submission);
  };

  const columns: ColumnDef<ResponsiveSubmissionItem>[] = [
    {
      field: 'user',
      headerName: 'Name',
      width: '10%',
      renderCell: (submission) => {
        const name = getSubmissionName(submission);
        return (
          <Box sx={styles.studentIdentity}>
            <Avatar
              altText={name}
              fallbackText={name.charAt(0).toUpperCase()}
              src={submission.user.avatar}
              sx={styles.avatar}
            />
            <Typography noWrap sx={styles.name} title={name}>
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'receivedAt',
      headerName: 'Submission Date',
      width: '35%',
      renderCell: (submission) => getItemDetails(submission).date,
    },
    {
      field: 'displayGrade',
      headerName: 'Grade',
      width: '20%',
      renderCell: (submission) => getItemDetails(submission).grade,
    },
    {
      field: 'id',
      headerName: '',
      width: 56,
      align: 'right',
      renderCell: (submission) => {
        const { capabilities, hasSubmission, name } =
          getItemDetails(submission);
        return (
          <SubmissionActions
            canDeleteSubmission={capabilities.canDelete}
            hasSubmission={hasSubmission}
            onDeleteSubmission={
              capabilities.canDelete && onDeleteSubmission
                ? () => onDeleteSubmission(submission)
                : undefined
            }
            provider={provider}
            submissionId={submission.id}
            studentFirstName={submission.user.firstName}
            studentName={name}
          />
        );
      },
    },
  ];

  const renderTable = (submissions: ResponsiveSubmissionItem[]) => {
    const canOpenRows = submissions.some(
      (submission) => getItemDetails(submission).capabilities.canOpen
    );

    return (
      <DataTable
        clickableRows={canOpenRows}
        columns={columns}
        containerStyle={styles.table}
        getRowId={(submission) => submission.id || submission.user.id}
        headerStyle={styles.tableHeader}
        onRowClick={openSubmission}
        rows={submissions}
      />
    );
  };

  const renderCards = (submissions: ResponsiveSubmissionItem[]) => (
    <Box sx={styles.cards}>
      {submissions.map((submission) => {
        const { capabilities, date, grade, hasSubmission, name } =
          getItemDetails(submission);
        return (
          <Box
            aria-label={`${name} submission`}
            component="article"
            key={submission.id || submission.user.id}
            onClick={() => openSubmission(submission)}
            onKeyDown={(event) => handleKeyDown(event, submission)}
            role={capabilities.canOpen ? 'button' : undefined}
            sx={{
              ...styles.card,
              ...(capabilities.canOpen ? styles.clickableCard : {}),
            }}
            tabIndex={capabilities.canOpen ? 0 : undefined}
          >
            <Box sx={styles.cardHeader}>
              <Typography sx={styles.cardName}>{name}</Typography>
              <SubmissionActions
                canDeleteSubmission={capabilities.canDelete}
                hasSubmission={hasSubmission}
                onDeleteSubmission={
                  capabilities.canDelete && onDeleteSubmission
                    ? () => onDeleteSubmission(submission)
                    : undefined
                }
                provider={provider}
                submissionId={submission.id}
                studentFirstName={submission.user.firstName}
                studentName={name}
              />
            </Box>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                Submitted on:{' '}
              </Box>
              {date}
            </Typography>
            <Typography sx={styles.detail}>
              <Box component="span" sx={styles.detailLabel}>
                Grade:{' '}
              </Box>
              {grade}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderSectionContent = (
    submissions: ResponsiveSubmissionItem[],
    hideIdentities = false
  ) => {
    if (!submissions.length) {
      return (
        <Typography sx={styles.empty}>No students in this category</Typography>
      );
    }

    if (hideIdentities) {
      return (
        <Typography sx={styles.empty}>
          {submissions.length} students have not submitted
        </Typography>
      );
    }

    return isDesktop ? renderTable(submissions) : renderCards(submissions);
  };

  const renderGroupedSection = (key: SectionKey) => {
    const groups = groupedSubmissionGroups?.[key] || [];
    if (!groups.length) {
      return (
        <Typography sx={styles.empty}>No groups in this category</Typography>
      );
    }

    if (key === 'noSubmissions' && hideNoSubmissionIdentities) {
      return (
        <Typography sx={styles.empty}>
          {groups.length} groups have not submitted
        </Typography>
      );
    }

    return (
      <Box sx={styles.nestedGroups}>
        {groups.map((group) => (
          <Accordion
            ariaLabel={`${group.name} (${group.submissions.length})`}
            arrowPosition="left"
            key={group.id || group.name}
            label={`${group.name} (${group.submissions.length})`}
            size="small"
            sx={styles.nestedAccordion}
          >
            {renderSectionContent(group.submissions)}
          </Accordion>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={styles.wrapper}>
      {sections.map(({ key, label }) => {
        const count = isGroupAssignment
          ? groupedSubmissionGroups?.[key]?.length || 0
          : submissionGroups[key].length;
        return (
          <Accordion
            ariaLabel={`${label} (${count})`}
            arrowPosition="left"
            defaultExpanded={key === 'earlySubmissions'}
            key={key}
            label={`${label} (${count})`}
            size="medium"
            sx={styles.accordion}
          >
            {isGroupAssignment
              ? renderGroupedSection(key)
              : renderSectionContent(
                  submissionGroups[key],
                  key === 'noSubmissions' && hideNoSubmissionIdentities
                )}
          </Accordion>
        );
      })}
    </Box>
  );
};

export default SubmissionAccordions;
